/**
 * Coats of arms for the titles on the map, as they stood around 1212.
 *
 * The crest frames are hollow, and this is what goes inside them. A blazon
 * here is data rather than a picture: a field, and things standing on it.
 * `paint.ts` turns one into pixels, which means the same description serves
 * the shield beside a title in the panel and the banner drawn on a capital
 * when the map is pulled right out.
 *
 * On accuracy. Arms in 1212 are early: many houses had only just settled on
 * one, and plenty of the realms here never used western heraldry at all. So
 * three rules are followed and it is worth being straight about them.
 *
 *  1. Where a realm's arms are attested for the period, they are used, and
 *     the period detail is kept. France is semé-de-lis rather than the later
 *     three, because the reduction to three is a century away. Navarre wears
 *     the chains, which it takes at Las Navas de Tolosa in the July of this
 *     very year. England is three lions, settled by Richard a decade back.
 *  2. Where a realm used a banner rather than arms, the banner is blazoned as
 *     though it were arms. The Almohad chequy and the plain Ayyubid gold are
 *     both real standards, and both read on a shield.
 *  3. The de jure empires are titles rather than states, and mostly nobody
 *     wore them. Each takes the arms of the crown that would have the best
 *     claim to it, which is what a claimant would in fact have flown.
 *
 * Duchies are not listed. There are two hundred and fifty of them, most named
 * for a market town, and inventing arms for each would be two hundred and
 * fifty small lies. They are differenced from their kingdom instead, the way
 * a cadet branch really did it: the same arms with a bordure, a change of
 * tincture, or a label across the chief.
 */

export type Tincture =
  | 'or'
  | 'argent'
  | 'gules'
  | 'azure'
  | 'sable'
  | 'vert'
  | 'purpure'
  | 'tenne';

export const TINCTURES: Record<Tincture, string> = {
  or: '#d8a72b',
  argent: '#e9e6dd',
  gules: '#a5262b',
  azure: '#2a4a8f',
  sable: '#241f1c',
  vert: '#2f6b39',
  purpure: '#6b2f6b',
  tenne: '#9c5a22',
};

/** Every device the painter can draw. */
export type ChargeKind =
  | 'lionRampant'
  | 'lionPassant'
  | 'eagle'
  | 'eagleDoubled'
  | 'fleur'
  | 'cross'
  | 'crossPotent'
  | 'crossPatty'
  | 'saltire'
  | 'castle'
  | 'crown'
  | 'keys'
  | 'escutcheon'
  | 'crescent'
  | 'mullet'
  | 'trident'
  | 'axe'
  | 'roundel'
  | 'harp';

export type Field =
  | { t: 'plain'; a: Tincture }
  | { t: 'perPale'; a: Tincture; b: Tincture }
  | { t: 'perFess'; a: Tincture; b: Tincture }
  | { t: 'quarterly'; a: Tincture; b: Tincture }
  /** Horizontal bars, `n` of them, starting with `a` at the chief. */
  | { t: 'barry'; a: Tincture; b: Tincture; n: number }
  /** Vertical pales, `n` of them. */
  | { t: 'paly'; a: Tincture; b: Tincture; n: number }
  | { t: 'chequy'; a: Tincture; b: Tincture; n: number }
  /** Strewn with small charges, as France is with lilies. */
  | { t: 'seme'; a: Tincture; charge: ChargeKind; on: Tincture };

export interface Charge {
  k: ChargeKind;
  a: Tincture;
  /** 1, 3 or 5. Three sit in pale, five in cross. Defaults to 1. */
  n?: number;
  /** Outline colour, for a charge that would otherwise vanish on its field. */
  edge?: Tincture;
  /** Scale against the default size for this charge. */
  scale?: number;
}

export interface Arms {
  field: Field;
  charges?: Charge[];
  /** A ring inside the shield's edge, as Scotland's tressure. */
  bordure?: Tincture;
  /** A short bar across the top, the classic mark of a junior branch. */
  label?: Tincture;
}

/** Quarterly arms: four different shields in one. Rendered by recursion. */
export interface QuarteredArms {
  quarters: [Arms, Arms, Arms, Arms];
}

export type AnyArms = Arms | QuarteredArms;

export function isQuartered(arms: AnyArms): arms is QuarteredArms {
  return (arms as QuarteredArms).quarters !== undefined;
}

/**
 * The crowns, by the kingdom id the bake gave them.
 *
 * Anything absent falls back to the arms of its empire, and failing that to a
 * plain field picked from its name, so no title is ever left blank.
 */
export const KINGDOM_ARMS: Record<string, AnyArms> = {
  // --- Britannia -----------------------------------------------------------
  // Richard settled on three lions in 1198; John is flying them now.
  k_england: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionPassant', a: 'or', n: 3 }] },
  // The tressure is the point of the Scottish arms and the painter has one.
  k_scotland: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'lionRampant', a: 'gules' }], bordure: 'gules' },
  /*
   * Vert, a harp Or.
   *
   * Was azure with a plain white roundel, which was a placeholder standing in
   * for arms nobody had drawn. Strictly the heraldic arms of Ireland are
   * *azure* a harp or, and that is what sits in the royal quarterings - but
   * the green harp is the flag the island is actually known by, and on the
   * political map it makes Ireland green, which is where anyone looking for it
   * will expect to find it.
   */
  k_ireland: { field: { t: 'plain', a: 'vert' }, charges: [{ k: 'harp', a: 'or' }] },

  // --- Francia -------------------------------------------------------------
  // France Ancient: strewn with lilies, not the later three.
  k_france: { field: { t: 'seme', a: 'azure', charge: 'fleur', on: 'or' } },
  k_aquitaine: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionPassant', a: 'or' }] },
  k_occitania: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'crossPatty', a: 'or' }] },

  // --- Hispania ------------------------------------------------------------
  k_castile: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'castle', a: 'or' }] },
  k_leon: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'lionRampant', a: 'purpure' }] },
  // Five escutcheons in cross, each charged with the wounds of Christ.
  k_portugal: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'escutcheon', a: 'azure', n: 5 }] },
  // The chains, taken at Las Navas de Tolosa in July of this year.
  k_navarre: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'saltire', a: 'or' }] },
  k_aragon: { field: { t: 'paly', a: 'or', b: 'gules', n: 9 } },
  // Almohad Iberia flies the caliph's standard, chequy in gold and red.
  k_andalusia: { field: { t: 'chequy', a: 'or', b: 'gules', n: 6 } },
  k_valencia: { field: { t: 'paly', a: 'or', b: 'gules', n: 9 }, charges: [{ k: 'crown', a: 'or' }] },

  // --- Germania ------------------------------------------------------------
  k_germany: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'eagle', a: 'sable' }] },
  k_burgundy: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'lionRampant', a: 'or' }] },
  // Bohemia's lion is queue fourchee and crowned; a crowned lion carries it.
  k_bohemia: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionRampant', a: 'argent' }] },

  // --- Italia --------------------------------------------------------------
  // The Lombard League's cross, which is Milan's and half of Italy's besides.
  k_italy: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'cross', a: 'gules' }] },
  // Frederick is king of Sicily and emperor both, so the imperial eagle.
  k_sicily: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'eagle', a: 'sable' }] },
  k_papal: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'keys', a: 'or' }] },
  k_venice: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionPassant', a: 'or' }] },

  // --- Scandinavia ---------------------------------------------------------
  // Valdemar's lions, on a field strewn with hearts; the roundels stand for
  // the seeblatter the hearts began as.
  k_denmark: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'lionPassant', a: 'azure', n: 3 }] },
  k_norway: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'axe', a: 'or' }] },
  // The three crowns are later; the Folkung lion is the safer device here.
  k_sweden: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'lionRampant', a: 'or' }] },

  // --- Carpathia -----------------------------------------------------------
  k_hungary: { field: { t: 'barry', a: 'gules', b: 'argent', n: 8 } },
  k_poland: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'eagle', a: 'argent' }] },

  // --- Rus -----------------------------------------------------------------
  // The Rurikid bident, which every branch of the house marked its coins with.
  k_kyiv: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'trident', a: 'or' }] },
  k_vladimir: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionRampant', a: 'or' }] },
  k_novgorod: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'crown', a: 'or' }] },
  k_galicia: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'lionRampant', a: 'or' }] },

  // --- Byzantium -----------------------------------------------------------
  // The tetragrammatic cross, four fire-steels in the quarters.
  k_thrace: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'cross', a: 'or' }, { k: 'roundel', a: 'or', n: 4, scale: 0.34 }] },
  k_thessalonica: { field: { t: 'plain', a: 'purpure' }, charges: [{ k: 'cross', a: 'or' }] },
  k_achaea: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'crossPatty', a: 'argent' }] },
  k_epirus: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'eagleDoubled', a: 'or' }] },
  k_nicaea: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'eagleDoubled', a: 'or' }] },
  k_trebizond: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'eagle', a: 'sable' }] },
  k_bulgaria: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'lionRampant', a: 'argent' }] },
  k_serbia: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'eagleDoubled', a: 'argent' }] },

  // --- Outremer and the east ----------------------------------------------
  // Gold on silver, which no other arms in Christendom are allowed to be.
  k_jerusalem: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'crossPotent', a: 'or' }] },
  k_antioch: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'cross', a: 'argent' }] },
  k_cyprus: { field: { t: 'barry', a: 'or', b: 'gules', n: 8 }, charges: [{ k: 'lionRampant', a: 'argent' }] },
  k_cilicia: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'lionRampant', a: 'gules' }] },
  // Georgia's five crosses: Saint George's, and four in the quarters.
  k_georgia: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'cross', a: 'gules' }, { k: 'crossPatty', a: 'gules', n: 4, scale: 0.3 }] },

  // --- The Muslim powers ---------------------------------------------------
  // The Almohad standard taken at Las Navas, chequy gold and red.
  k_morocco: { field: { t: 'chequy', a: 'or', b: 'gules', n: 6 } },
  k_tlemcen: { field: { t: 'plain', a: 'vert' }, charges: [{ k: 'crescent', a: 'argent' }] },
  k_ifriqiya: { field: { t: 'plain', a: 'gules' }, charges: [{ k: 'crescent', a: 'or' }] },
  // The Ayyubid banner was a plain field of gold, and the eagle is Saladin's.
  k_egypt: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'eagle', a: 'gules' }] },
  k_baghdad: { field: { t: 'plain', a: 'sable' } },
  k_mosul: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'crescent', a: 'or' }] },
  k_mardin: { field: { t: 'plain', a: 'tenne' }, charges: [{ k: 'mullet', a: 'or' }] },
  k_azerbaijan: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'mullet', a: 'argent' }] },
  // The Seljuks of Rum struck the double eagle on everything they built.
  k_rum: { field: { t: 'plain', a: 'azure' }, charges: [{ k: 'eagleDoubled', a: 'or' }] },

  // --- The northern edge ---------------------------------------------------
  k_livonia: { field: { t: 'plain', a: 'argent' }, charges: [{ k: 'cross', a: 'gules' }] },
};

/**
 * The empires.
 *
 * Only Germania, Byzantium and the Maghreb were worn by anybody in 1212. The
 * rest take the arms of the crown with the strongest claim on them, which is
 * what a claimant would actually have raised.
 */
export const EMPIRE_ARMS: Record<string, AnyArms> = {
  e_britannia: KINGDOM_ARMS.k_england!,
  e_francia: KINGDOM_ARMS.k_france!,
  // Castile and Leon quartered, as the united crown would bear them.
  e_hispania: {
    quarters: [
      KINGDOM_ARMS.k_castile as Arms,
      KINGDOM_ARMS.k_leon as Arms,
      KINGDOM_ARMS.k_leon as Arms,
      KINGDOM_ARMS.k_castile as Arms,
    ],
  },
  e_germania: { field: { t: 'plain', a: 'or' }, charges: [{ k: 'eagle', a: 'sable', scale: 1.1 }] },
  e_italia: KINGDOM_ARMS.k_italy!,
  e_scandinavia: KINGDOM_ARMS.k_denmark!,
  e_carpathia: KINGDOM_ARMS.k_hungary!,
  e_rus: KINGDOM_ARMS.k_kyiv!,
  e_byzantium: KINGDOM_ARMS.k_thrace!,
  e_maghreb: KINGDOM_ARMS.k_morocco!,
  e_arabia: KINGDOM_ARMS.k_egypt!,
  e_anatolia: KINGDOM_ARMS.k_rum!,
};

const FALLBACK_FIELDS: Tincture[] = ['gules', 'azure', 'vert', 'sable', 'purpure', 'tenne'];
const FALLBACK_CHARGES: ChargeKind[] = ['lionRampant', 'eagle', 'crossPatty', 'mullet', 'crescent', 'castle', 'fleur'];

/** A stable number from a string, so a title's arms never change between loads. */
function hashOf(text: string): number {
  let h = 2_166_136_261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16_777_619);
  }
  return (h >>> 0) / 0x1_0000_0000;
}

/**
 * A duchy's arms: its kingdom's, differenced.
 *
 * This is how a junior branch actually did it. The duke of a house bore his
 * king's arms with a mark that said which son he was: a bordure round the
 * edge, a label across the top, or a change of tincture. Doing the same here
 * means a duchy always looks like it belongs to its kingdom, which is exactly
 * the thing the map should be showing, and it costs no table.
 *
 * A duchy under no crown gets arms of its own, built from its name.
 */
/**
 * Duchies that bore their own arms, where the record is good enough to draw.
 *
 * Everything else is differenced from its crown, which is the honest medieval
 * default for a cadet. But the famous duchies were not cadets in anyone's
 * eyes, and a Flanders wearing France's lilies with a border would be wrong
 * in the way players notice. Each entry says how far it can be trusted.
 */
export const DUCHY_ARMS: Record<string, AnyArms> = {
  // Or, a lion rampant sable: on Flemish seals from the 1160s. Solid.
  'County of Flanders': {
    field: { t: 'plain', a: 'or' },
    charges: [{ k: 'lionRampant', a: 'sable' }],
  },
  // Chequy or and azure within a bordure gules: the Dreux arms Peter takes to
  // Brittany in 1213, one year from now. Close enough to fly.
  'Duchy of Brittany': { field: { t: 'chequy', a: 'or', b: 'azure', n: 6 }, bordure: 'gules' },
  // Barry sable and or: the Ascanian dukes' coat. The green crancelin that
  // lies across it is beyond this painter, so the bars carry it alone.
  'Duchy of Saxony': { field: { t: 'barry', a: 'sable', b: 'or', n: 10 } },
  // The blue and white lozenges are 1242, and lozengy is beyond the painter
  // anyway, so chequy stands in: thirty years early and the wrong lattice,
  // but the only device anyone will ever read as Bavaria.
  'Duchy of Bavaria': { field: { t: 'chequy', a: 'argent', b: 'azure', n: 7 } },
  // The chequy attributed to Croatia's old kings. Attested late, but it is
  // the ground the country's whole heraldry stands on.
  'Duchy of Croatia': { field: { t: 'chequy', a: 'argent', b: 'gules', n: 6 } },
  // Gules, three seaxes argent: the arms attributed to the old kingdom of
  // Essex. The painter has no seax, so its axe swings in their place.
  'Duchy of Essex': {
    field: { t: 'plain', a: 'gules' },
    charges: [{ k: 'axe', a: 'argent', n: 3 }],
  },
  // Paly or and gules: the count in 1212 is a cadet of Barcelona and bears
  // the pales, the same coat Aragon flies. True, and confusing on purpose.
  'County of Provence': { field: { t: 'paly', a: 'or', b: 'gules', n: 9 } },
  // Barry argent and azure: how the armorials of the next century record the
  // king of Iceland's arms.
  'Duchy of Iceland': { field: { t: 'barry', a: 'argent', b: 'azure', n: 12 } },
};

/** Tinctures a difference mark may borrow, in rotation. */
const DIFFERENCE_TINCTURES: Tincture[] = ['argent', 'sable', 'gules', 'azure', 'or', 'vert'];

/** Every tincture a field shows, so a mark can be made to contrast with all of them. */
function fieldTinctures(field: Field): Tincture[] {
  if (field.t === 'seme') return [field.a, field.on];
  if (field.t === 'plain') return [field.a];
  return [field.a, field.b];
}

/**
 * A mark tincture that cannot vanish into the field.
 *
 * The old rule was argent or sable for everyone, which put an argent label on
 * Leon's argent field and a sable bordure on Baghdad's sable one: a difference
 * present in the data and absent from the screen.
 */
function pickDifference(name: string, field: Field): Tincture {
  const shown = new Set(fieldTinctures(field));
  const usable = DIFFERENCE_TINCTURES.filter((t) => !shown.has(t));
  return usable[Math.floor(hashOf(`${name}:tincture`) * usable.length) % usable.length]!;
}

export function duchyArms(duchyName: string, kingdomArms: AnyArms | null): AnyArms {
  const own = DUCHY_ARMS[duchyName];
  if (own) return own;

  const roll = hashOf(duchyName);
  if (!kingdomArms) {
    const field = FALLBACK_FIELDS[Math.floor(roll * FALLBACK_FIELDS.length)]!;
    const charge = FALLBACK_CHARGES[Math.floor(hashOf(`${duchyName}:charge`) * FALLBACK_CHARGES.length)]!;
    return {
      field: { t: 'plain', a: field },
      charges: [{ k: charge, a: field === 'or' ? 'sable' : 'or' }],
    };
  }
  // A quartered coat cannot carry a bordure in this model, so the duchy takes
  // its crown's first quarter, which is how a herald would read the coat
  // aloud anyway, and differences that.
  if (isQuartered(kingdomArms)) {
    const first = kingdomArms.quarters[0];
    return { ...first, bordure: pickDifference(duchyName, first.field) };
  }
  const tincture = pickDifference(duchyName, kingdomArms.field);
  const base: Arms = { ...kingdomArms };
  return roll < 0.55 ? { ...base, bordure: tincture } : { ...base, label: tincture };
}

/** Arms for any title, with a sensible answer for every case. */
export function armsFor(
  tier: 'county' | 'duchy' | 'kingdom' | 'empire',
  name: string,
  kingdomId: string | null,
  empireId: string | null,
): AnyArms | null {
  if (tier === 'empire') return (empireId && EMPIRE_ARMS[empireId]) || null;
  if (tier === 'kingdom') {
    if (kingdomId && KINGDOM_ARMS[kingdomId]) return KINGDOM_ARMS[kingdomId]!;
    return (empireId && EMPIRE_ARMS[empireId]) || null;
  }
  if (tier === 'duchy') {
    const crown = (kingdomId && KINGDOM_ARMS[kingdomId]) || null;
    return duchyArms(name, crown);
  }
  // Counties have no arms, and the county frame is solid for that reason.
  return null;
}
